/* eslint-disable @typescript-eslint/no-explicit-any */
import { RadioGroup } from "@mui/material";

import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { useTranslation } from "react-i18next";

import { useMemo, useRef, useState } from "react";

import { Icon, Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { formatTimestamp } from "@/utils/formatTimestamp";

import { classNames } from "@/utils/classNames";

import { useSocket } from "../../../context/SocketContext";
import { DecisionConfirmationTypes } from "../../../enums/DecisionConfirmationTypes.enum";
import { ScenarioActionTypes } from "../../../enums/ScenarioActionTypes.enum";
import { ScenarioMessageTypes } from "../../../enums/ScenarioMessageTypes.enum";
import { IDecisionInfo, IDecisionOption } from "../../../interfaces/IDecisionInfo.interface";

interface IChatDecision {
	messageId: string;
	decisionInfo: IDecisionInfo;
	timestamp: number;
	requireExplanation: boolean;
	onActionClick: (type: ScenarioMessageTypes, messageId: string, data: unknown) => void;
	currentPhaseId: number;
	isLastPhase: boolean;
	chatContainerRef: React.RefObject<HTMLDivElement | null>;
}

export default function ChatDecision({
	requireExplanation,
	decisionInfo,
	onActionClick,
	messageId,
	timestamp,
	isLastPhase,
	chatContainerRef,
}: IChatDecision) {
	const { t, i18n } = useTranslation();
	const { currentUserData, errInfo, sendScenarioAction } = useSocket();
	const buttonRef = useRef<HTMLDivElement>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalText, setModalText] = useState("");
	const ts = (key: string) => t(`courses.${key}`);
	const currentUserId = currentUserData?.id ?? -1;
	const isResponsibleForEmergency = errInfo?.id === currentUserId;
	let isShowNextPhase = false;

	const scrollToDecisionButton = () => {
		const buttonPosition = buttonRef.current?.getBoundingClientRect();
		const chatContainerPosition = chatContainerRef.current?.getBoundingClientRect();

		chatContainerRef.current?.scrollTo({
			top:
				(buttonRef.current?.offsetTop ?? 0) -
				(chatContainerPosition?.height ?? 0) +
				(buttonPosition?.height ?? 0),
			behavior: "smooth",
		});
	};

	const chosenOption = useMemo(() => {
		return decisionInfo?.decisionOptions?.find((item) => item.userVotedIds.includes(currentUserId));
	}, [decisionInfo, currentUserId]);

	const handleOptionChange = (selectedOption: IDecisionOption) => {
		if (
			(decisionInfo.confirmationRequired === DecisionConfirmationTypes.FROM_LEADER &&
				!isResponsibleForEmergency) ||
			decisionInfo?.finalDecision
		) {
			return;
		}

		if (selectedOption.userVotedIds.some((userId) => userId === currentUserId)) {
			return;
		}

		const updatedOptions = decisionInfo.decisionOptions.map((option) => {
			if (option.option === selectedOption.option) {
				return {
					...option,
					userVotedIds: [...option?.userVotedIds, currentUserId],
				};
			}

			return {
				...option,
				userVotedIds: option?.userVotedIds?.filter((userId) => userId !== currentUserId),
			};
		});

		onActionClick(ScenarioMessageTypes.DECISION, messageId, {
			...decisionInfo,
			decisionOptions: updatedOptions,
		});
		setTimeout(() => {
			scrollToDecisionButton();
		}, 100);
	};

	// if err vote for something
	if (
		decisionInfo?.confirmationRequired === DecisionConfirmationTypes.FROM_LEADER &&
		decisionInfo.decisionOptions?.some((item) => item.userVotedIds.includes(currentUserId)) &&
		isResponsibleForEmergency
	) {
		isShowNextPhase = true;
		// if someone vote for something
	} else if (
		decisionInfo?.confirmationRequired === DecisionConfirmationTypes.FROM_ALL &&
		decisionInfo.decisionOptions?.some((item) => item.userVotedIds.includes(currentUserId)) &&
		isResponsibleForEmergency
	) {
		// const maxVotes = Math.max(
		// 	...decisionInfo.decisionOptions.map((item) => item.userVotedIds.length)
		// );
		// const optionsWithMaxVotes = decisionInfo.decisionOptions.filter(
		// 	(item) => item.userVotedIds.length === maxVotes
		// );

		// isShowNextPhase = maxVotes > 0 && optionsWithMaxVotes.length === 1;
		isShowNextPhase = true;
	}

	const handleOpenModal = () => {
		setIsModalOpen(true);

		onActionClick(ScenarioMessageTypes.DECISION, messageId, {
			...decisionInfo,
			block: true,
		});
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setModalText("");
		onActionClick(ScenarioMessageTypes.DECISION, messageId, {
			...decisionInfo,
			block: false,
		});
	};

	const handleConfirm = () => {
		onNextPhaseClick();

		setIsModalOpen(false);
		setModalText("");
	};

	const onNextPhaseClick = () => {
		let finalDecision: IDecisionOption | null = null;

		if (
			[DecisionConfirmationTypes.FROM_LEADER, DecisionConfirmationTypes.FROM_ALL].includes(
				decisionInfo?.confirmationRequired
			)
		) {
			const votedByErr = decisionInfo.decisionOptions?.find((item) =>
				item.userVotedIds.includes(currentUserId)
			);

			if (votedByErr) {
				finalDecision = votedByErr;
			}
		} else if (decisionInfo?.confirmationRequired === DecisionConfirmationTypes.NONE) {
			const decision = decisionInfo.decisionOptions?.[0];

			if (decision) {
				finalDecision = decision;
			}
		}

		if (finalDecision) {
			onActionClick(ScenarioMessageTypes.DECISION, messageId, {
				...decisionInfo,
				finalDecision,
				explanationText: modalText,
				block: false,
			});
			if (!isLastPhase) {
				sendScenarioAction(ScenarioActionTypes.NEW_STAGE, {
					nextStageId: finalDecision.phaseId,
				});
			}
		}
	};

	const generalVotesLength = decisionInfo.decisionOptions.reduce(
		(acc, item) => acc + item.userVotedIds.length,
		0
	);

	const nextPhaseButtonTitle =
		decisionInfo?.confirmationRequired === DecisionConfirmationTypes.NONE
			? (decisionInfo?.decisionOptions?.[0]?.option ?? ts("progress.next"))
			: ts("progress.confirm");

	if (
		decisionInfo?.confirmationRequired === DecisionConfirmationTypes.NONE &&
		!!decisionInfo?.finalDecision?.option
	) {
		return <>{decisionInfo?.decisionOptions?.[0]?.option ?? ts("progress.next")}</>;
	}

	return (
		<div className={`mt-2 space-y-2 ${decisionInfo?.finalDecision ? "pr-32" : ""}`}>
			{decisionInfo?.confirmationRequired === DecisionConfirmationTypes.NONE &&
			!isResponsibleForEmergency ? (
				<></>
			) : (
				<div className="flex justify-between px-1">
					<p className="text-[0.875rem] text-primary-gray font-medium">{decisionInfo.name}</p>
					<p
						className={`text-[0.75rem] text-primary-gray-lighter ${
							decisionInfo?.finalDecision ? "" : "pr-32"
						}`}
					>
						{formatTimestamp(timestamp)}
					</p>
				</div>
			)}
			{decisionInfo?.confirmationRequired !== DecisionConfirmationTypes.NONE && (
				<RadioGroup className="space-y-[6px]">
					{decisionInfo?.decisionOptions
						.filter((optionData) =>
							decisionInfo?.finalDecision
								? decisionInfo?.finalDecision?.option === optionData.option
								: true
						)
						.map((option, index) => {
							const isSelected = decisionInfo?.finalDecision
								? false
								: option?.userVotedIds?.includes(currentUserId) ||
									(decisionInfo.confirmationRequired === DecisionConfirmationTypes.FROM_LEADER &&
										option?.userVotedIds?.includes(errInfo?.id ?? -1));

							const votedPercent =
								+((option?.userVotedIds?.length ?? 0) / (generalVotesLength || 1)).toFixed(2) * 100;

							return (
								<div key={index} className="flex w-full gap-3 items-center">
									<div
										className={classNames(
											`shadow-card bg-white flex-1 text-primary-gray px-[14px] py-[10px] rounded-lg flex justify-between items-center ${
												isSelected && "border-[1px] border-accent"
											}`,
											decisionInfo?.block ? "opacity-50" : ""
										)}
										onClick={() => !decisionInfo?.block && handleOptionChange(option)}
									>
										<p className={`text-[14px] text-primary-gray`}>
											<span className="font-semibold">
												Option {decisionInfo?.finalDecision ? "" : index + 1}:{" "}
											</span>{" "}
											{option.option}
										</p>
										{decisionInfo?.finalDecision ? (
											<></>
										) : isSelected ? (
											<p className="w-5 h-5 min-w-5 rounded-full flex items-center justify-center bg-primary-green">
												<Icon className="text-white w-[14px] h-[14px]" icon={faCheck} />
											</p>
										) : (
											<p className="w-5 h-5 min-w-5 rounded-full bg-white border-[1px] border-grey-soft"></p>
										)}
									</div>
									{decisionInfo?.finalDecision ? (
										<></>
									) : decisionInfo.confirmationRequired === DecisionConfirmationTypes.FROM_ALL ? (
										<div className="flex items-center justify-center gap-1 w-20 mr-8 px-[10px] border-[1px] h-7 border-border-soft rounded-[50px]">
											<span className="bg-primary-blue w-1 h-1 rounded-full"></span>
											<p className="text-primary-gray text-[12px] font-semibold flex items-center justify-center shadow-card backdrop-blur-[20px]">
												{option?.userVotedIds?.length ?? 0}{" "}
												<span className="ml-[2px] text-[12px]">({votedPercent}%)</span>
											</p>
										</div>
									) : (
										<p className="pr-[120px]" />
									)}
								</div>
							);
						})}
				</RadioGroup>
			)}
			{decisionInfo.explanationText ? (
				<div className="flex w-full gap-3 items-center">
					<div
						className={`shadow-card bg-white flex-1 text-primary-gray px-[14px] py-[10px] rounded-lg flex justify-between items-center
										}`}
					>
						<p className={`text-[14px] text-primary-gray`}>
							<span className="font-semibold">
								{ts("progress.finalDecision.elaboratedDecision")} {" : "}
							</span>{" "}
							{decisionInfo.explanationText}
						</p>
					</div>
				</div>
			) : (
				<></>
			)}

			{isResponsibleForEmergency &&
				(decisionInfo?.confirmationRequired === DecisionConfirmationTypes.NONE ||
					(isShowNextPhase && !decisionInfo?.finalDecision)) && (
					<div ref={buttonRef}>
						<Button
							className={`h-[44px] !py-3 ${
								decisionInfo?.confirmationRequired === DecisionConfirmationTypes.NONE
									? "!w-[calc(100%-128px)]"
									: "!w-fit !max-w-[40vw]"
							} mt-[6px] !font-medium !text-wrap`}
							color={ButtonColor.DECISION}
							disabled={
								decisionInfo?.confirmationRequired === DecisionConfirmationTypes.NONE &&
								!!decisionInfo?.finalDecision?.option
							}
							image={
								<p className="mr-2 w-4 h-4 rounded-full flex items-center justify-center border-[1.25px] border-white">
									<Icon className="text-white w-[8px] h-[8px]" icon={faCheck} />
								</p>
							}
							size={ButtonSize.ML}
							testId="new-user-button"
							title={
								i18n.exists(`courses.${nextPhaseButtonTitle}`)
									? ts(nextPhaseButtonTitle)
									: nextPhaseButtonTitle
							}
							onClick={requireExplanation ? handleOpenModal : onNextPhaseClick}
						/>
					</div>
				)}

			<Modal
				cancelButtonText={t("account.cancel")}
				disableSave={modalText?.length < 3}
				handleClose={handleCloseModal}
				handleSave={handleConfirm}
				isOpened={isModalOpen}
				size="md"
				submitButtonText={ts("progress.confirm")}
				subtitle={ts("progress.finalDecision.subtitle")}
				title={ts("progress.finalDecision.title")}
			>
				<div className="flex flex-col h-full space-y-4">
					<div className="flex-grow w-full">
						<div className="text-sm font-semibold text-primary-gray">{decisionInfo.name}</div>
						<span className="mb-2 text-sm font-normal text-primary-gray">
							{chosenOption?.option}
						</span>
					</div>

					<div className="space-y-2">
						<textarea
							className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
							placeholder={ts("progress.finalDecision.placeholder")}
							rows={4}
							value={modalText}
							onChange={(e) => setModalText(e.target.value)}
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
}
